/**
 * Collider component - defines collision radius for entities
 * Used for unit/building separation to prevent overlapping
 */

export interface ColliderComponent {
  radius: number;      // Collision radius
  isStatic: boolean;   // If true, entity cannot be pushed (buildings)
  mass: number;        // Affects how much entity resists being pushed
}

// Default collision radii for different entity types
// These are scaled to match the visual size of units at scale 1.75
export const UNIT_COLLISION_RADIUS = 0.4;  // Base unit radius
export const CAVALRY_COLLISION_RADIUS = 0.6;  // Cavalry is larger

// Building collision radii - matched to visual mesh sizes
export const BUILDING_COLLISION_RADII: Record<string, number> = {
  capital: 2.0,    // Visual: 3.8 x 3.8
  barracks: 1.4,   // Visual: 2.6 x 2.4
  tower: 1.5,      // Visual: 2.8 x 2.8
  mine: 1.6,       // Visual: ~3.2 diameter rocks
  farm: 1.5,       // Visual: 2.8 x 2.4
  market: 1.5,     // Visual: 2.8 x 2.6
  stable: 1.5,     // Visual: 2.8 x 2.4
  smithy: 1.5,     // Visual: 2.8 x 2.6
  temple: 1.4,     // Visual: 2.6 x 2.6
  archery: 1.6,    // Visual: 2.8 x 2.6 (with targets on front)
  spearcamp: 1.4,  // Visual: 2.6 x 2.4
  wall: 0.4,       // Visual: 1x1 tile wall segment
};

// Building defense values (damage reduction when attacked)
export const BUILDING_DEFENSE: Record<string, number> = {
  capital: 15,
  barracks: 5,
  tower: 10,
  mine: 3,
  farm: 2,
  market: 4,
  stable: 5,
  smithy: 6,
  temple: 5,
  archery: 5,
  spearcamp: 5,
  wall: 12,       // High defense for defensive structure
};
export const DEFAULT_BUILDING_COLLISION_RADIUS = 1.5;

export function createCollider(
  radius: number,
  isStatic: boolean = false,
  mass: number = 1.0
): ColliderComponent {
  return { radius, isStatic, mass };
}

/**
 * Get collision radius for a unit type
 */
export function getUnitCollisionRadius(unitType: string): number {
  if (unitType === 'cavalry') {
    return CAVALRY_COLLISION_RADIUS;
  }
  return UNIT_COLLISION_RADIUS;
}

/**
 * Get collision radius for a building type
 */
export function getBuildingCollisionRadius(buildingType: string): number {
  return BUILDING_COLLISION_RADII[buildingType] ?? DEFAULT_BUILDING_COLLISION_RADIUS;
}
